import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { ROUTES } from 'config/Nav';
import SocialMediaGroup from '../SocialMediaGroup/SocialMediaGroup';
import useStyles from './styles';
import clsx from 'clsx';
import { Link, SocialMediaLink, PublicUserInfo, Avatar } from '@elevator/ui';

export type UserCardVariant = 'contained' | 'fullWidth';

/**
 * UserCard component displays a user profile card with avatar, name, username, and optional social media links.
 * Used in Staff, Directory, and SearchResultUserList layouts to display user information in a card format.
 */
const UserCard = ({
  user,
  variant = 'contained',
  socialMedia,
  name,
}: UserCardPropTypes) => {
  const { classes } = useStyles();
  const displayName = user?.displayName || user?.preferredUsername || name;
  const username = user?.preferredUsername;
  const isFullWidth = variant === 'fullWidth';

  return (
    <Paper
      elevation={1}
      className={clsx(
        classes.root,
        isFullWidth ? classes.rootFullWidth : classes.rootContained,
      )}
      data-component="UserCard"
    >
      <Box className={classes.row}>
        <Box className={classes.avatarContainer}>
          {user ? (
            <Link
              href={ROUTES.USER.to + user.preferredUsername}
              aria-label={`View ${displayName}'s profile`}
            >
              <Avatar
                alt={user.preferredUsername}
                src={user.avatar?.sourceUrl}
                srcSet={user.avatar?.sourceSet}
                size="large"
                username={displayName}
              />
            </Link>
          ) : (
            <Avatar size="large" username={name} />
          )}
        </Box>

        <Box className={classes.userMeta}>
          <Box className={classes.userMetaText}>
            <Typography display="block" variant="subtitle2">
              {name}
            </Typography>
            {user && username && (
              <Link
                href={ROUTES.USER.to + user.preferredUsername}
                aria-label={`View ${username}'s profile`}
              >
                <Typography variant="subtitle2" color="secondary">
                  @{username}
                </Typography>
              </Link>
            )}
          </Box>
          {socialMedia && socialMedia.length > 0 && (
            <SocialMediaGroup socialMedia={socialMedia} variant="medium" />
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export type UserCardPropTypes = {
  name: string;
  user?: PublicUserInfo;
  variant?: UserCardVariant;
  socialMedia?: SocialMediaLink[];
};

export default UserCard;
